import { useEffect, useSyncExternalStore } from 'react';
import { api, type MailboxView } from '../api/client';
import { alertVoicemail, clearNotificationsTagged } from './alerts';
import { announceHere } from './changes';
import { useMessagesLive } from './messages';

/**
 * New voicemail, live (1.12.3). Stitch tells the people on a mailbox the moment a message is in it
 * (a 'voicemail' event on their live stream). Every open Stitch then:
 *
 * - shows a notice at the top (who called, how long) with Play, which plays it right there, and
 *   Open, which goes to the voicemail list;
 * - loads its voicemail lists, Activity and the Calls badge again;
 * - plays the message sound while in front, or shows a notification while it isn't (one tab).
 *
 * The count of messages not yet heard (the Calls badge, My phone in the menu) comes from the
 * mailbox, read when Stitch opens and again on each new voicemail, and goes down as they're played.
 *
 * Played or deleted on a desk phone (1.12.11): the phone system doesn't tell Stitch, so while a
 * notice is on screen Stitch looks at the mailbox again (every half minute while Stitch is in front,
 * and on coming back to it), and each time a voicemail list loads. A notice whose message is gone,
 * or already heard, goes with its notification on this device, and the count follows.
 */
export interface VoicemailNotice {
  tenant: string;
  mailbox: string;
  messageId: string;
  /** Who left it: their name, or their number as people write it. */
  caller: string;
  /** Their number (an extension when it's a coworker). */
  number: string;
  /** Seconds. */
  duration: number;
  at: string;
  /** Where the full list is (the push's address). */
  url: string;
}

interface State {
  notices: VoicemailNotice[];
  /** Messages not yet heard, per company (only the person's own mailbox). */
  unheard: Record<string, number>;
}

let state: State = { notices: [], unheard: {} };
/** When each notice came, on this device's clock ("<tenant>|<message id>"). */
const shownAt = new Map<string, number>();
const keyOf = (tenant: string, messageId: string) => `${tenant.toLowerCase()}|${messageId}`;
/** A message's notification (the push's tag, and the one the open Stitch shows). */
const tagOf = (mailbox: string, messageId: string) => `vm:${mailbox}:${messageId}`;
const listeners = new Set<() => void>();
function set(next: Partial<State>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useVoicemailNotices(): VoicemailNotice[] {
  return useSyncExternalStore(
    subscribe,
    () => state.notices,
    () => state.notices
  );
}

/** Messages in the person's own mailbox not yet heard, at this company. */
export function useUnheardVoicemail(tenant: string): number {
  return useSyncExternalStore(
    subscribe,
    () => state.unheard[tenant.toLowerCase()] ?? 0,
    () => 0
  );
}

export function dismissVoicemail(messageId: string): void {
  set({ notices: state.notices.filter((n) => n.messageId !== messageId) });
}

/**
 * The notices a fresh look at the person's own mailbox shows are dealt with: the message is heard
 * now, or it isn't there any more (deleted on a desk phone, say). Only notices that were up before
 * the look began are judged, and never one being played here. A message missing from a first page
 * that stops short of it (a mailbox with more than a page) isn't taken as gone.
 */
export function staleNotices(
  notices: readonly VoicemailNotice[],
  tenant: string,
  box: Pick<MailboxView, 'messages' | 'more'>,
  readAt: number,
  shown: ReadonlyMap<string, number>,
  playing: ReadonlySet<string>
): VoicemailNotice[] {
  const t = tenant.toLowerCase();
  const byId = new Map(box.messages.map((m) => [m.id, m]));
  let oldest = Infinity;
  for (const m of box.messages) {
    const at = Date.parse(m.time);
    if (Number.isFinite(at) && at < oldest) oldest = at;
  }
  return notices.filter((n) => {
    const key = keyOf(t, n.messageId);
    if (n.tenant !== t || playing.has(key)) return false;
    if ((shown.get(key) ?? Infinity) > readAt) return false;
    const m = byId.get(n.messageId);
    if (m) return !m.isNew;
    const at = Date.parse(n.at);
    return !box.more || (Number.isFinite(at) && at >= oldest);
  });
}

/**
 * The person's own mailbox was just read (the count's look, a voicemail list, Activity): notices
 * for messages heard or deleted elsewhere go, with their notifications here, and the count is set.
 */
export function voicemailListed(tenant: string, box: Pick<MailboxView, 'messages' | 'more' | 'newCount'>, readAt: number): void {
  const t = tenant.toLowerCase();
  const stale = staleNotices(state.notices, t, box, readAt, shownAt, playedHere);
  const unheard = box.newCount ?? box.messages.filter((m) => m.isNew).length;
  if (!stale.length && state.unheard[t] === unheard) return;
  set({ notices: stale.length ? state.notices.filter((n) => !stale.includes(n)) : state.notices, unheard: { ...state.unheard, [t]: unheard } });
  for (const n of stale) {
    shownAt.delete(keyOf(t, n.messageId));
    void clearNotificationsTagged(tagOf(n.mailbox, n.messageId));
  }
}

/**
 * Messages played from a notice on this device. Playing one marks it heard, and every device is
 * told so, this one too: that word mustn't take the notice (and its sound) away mid-play, or after a
 * pause. It goes when it ends here, or is dismissed.
 */
const playedHere = new Set<string>();
export function playedVoicemail(tenant: string, messageId: string, on: boolean): void {
  const key = `${tenant.toLowerCase()}|${messageId}`;
  if (on) playedHere.add(key);
  else playedHere.delete(key);
}

/** A message was played (here or in a list): its notice goes, and the count drops. */
export function heardVoicemail(tenant: string, messageId: string, wasNew = true): void {
  const t = tenant.toLowerCase();
  const n = state.notices.find((x) => x.messageId === messageId && x.tenant === t);
  if (n) void clearNotificationsTagged(tagOf(n.mailbox, n.messageId));
  set({
    notices: state.notices.filter((n) => n.messageId !== messageId),
    ...(wasNew && state.unheard[t] ? { unheard: { ...state.unheard, [t]: Math.max(0, state.unheard[t]! - 1) } } : {})
  });
}

const loading = new Map<string, Promise<void>>();
/** When the mailbox was last looked at, per company (this device's clock). */
const lastLook = new Map<string, number>();
/** Reads the mailbox: the count of messages not yet heard, and which notices are dealt with. */
async function loadUnheard(tenant: string): Promise<void> {
  const t = tenant.toLowerCase();
  const running = loading.get(t);
  if (running) return running;
  const readAt = Date.now();
  lastLook.set(t, readAt);
  const p = api
    .voicemail(t)
    .then((box) => voicemailListed(t, box, readAt))
    .catch(() => undefined)
    .finally(() => loading.delete(t));
  loading.set(t, p);
  return p;
}

/** While a notice is on screen, the mailbox is looked at again this often (Stitch in front). */
export const LOOK_AGAIN_MS = 30_000;
/** Coming back to Stitch: the mailbox is looked at again when the last look is older than this. */
const BACK_AGAIN_MS = 60_000;

/** At most three notices on screen: the newest first. */
const MAX_NOTICES = 3;

/** For someone with a mailbox at this company, while Stitch is open. */
export function useVoicemailLive(tenant: string, on: boolean): void {
  const t = tenant.toLowerCase();
  const showing = useVoicemailNotices().some((n) => n.tenant === t);
  useEffect(() => {
    if (on && t) void loadUnheard(t);
  }, [on, t]);
  // Played or deleted on a desk phone: Stitch isn't told, so it looks. Coming back to Stitch (the
  // count may be behind too), and every half minute while a notice is up and Stitch is in front.
  useEffect(() => {
    if (!on || !t) return;
    const back = () => {
      if (document.visibilityState === 'visible' && Date.now() - (lastLook.get(t) ?? 0) > (showing ? 0 : BACK_AGAIN_MS)) void loadUnheard(t);
    };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    const timer = showing
      ? window.setInterval(() => {
          if (document.visibilityState === 'visible') void loadUnheard(t);
        }, LOOK_AGAIN_MS)
      : undefined;
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('focus', back);
      window.clearInterval(timer);
    };
  }, [on, t, showing]);
  useMessagesLive((e) => {
    if (e.type === 'reset' || e.type === 'hello') {
      void loadUnheard(t);
      return;
    }
    if (e.type !== 'voicemail' || e.tenant?.toLowerCase() !== t || !e.mailbox) return;
    // Played, noted, moved or deleted on another device (or moved here from another box): the
    // counts and lists follow, and a notice for a message that's dealt with goes.
    if (e.change) {
      const mine = e.change === 'heard' && playedHere.has(`${t}|${e.messageId}`);
      if (e.messageId && e.change !== 'note' && !mine) {
        set({ notices: state.notices.filter((n) => !(n.messageId === e.messageId && n.tenant === t)) });
        void clearNotificationsTagged(tagOf(e.mailbox, e.messageId));
      }
      announceHere({ tenant: t });
      void loadUnheard(t);
      return;
    }
    if (!e.messageId || state.notices.some((n) => n.messageId === e.messageId && n.tenant === t)) return;
    const notice: VoicemailNotice = {
      tenant: t,
      mailbox: e.mailbox,
      messageId: e.messageId,
      caller: e.caller || 'Unknown caller',
      number: e.from?.number ?? '',
      duration: typeof e.duration === 'number' ? e.duration : 0,
      at: e.at ?? new Date().toISOString(),
      url: e.alert?.url ?? `/t/${encodeURIComponent(t)}/me#voicemail`
    };
    shownAt.set(keyOf(t, e.messageId), Date.now());
    set({ notices: [notice, ...state.notices].slice(0, MAX_NOTICES) });
    // Lists on screen (My phone, Calls, Activity) load again, and the count is read again (the
    // message is in the mailbox by now: that's how Stitch heard of it).
    announceHere({ tenant: t });
    void loadUnheard(t);
    void alertVoicemail(e);
  }, on);
}

/** "0:24", "12:05". */
export function clockOf(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
